import { db } from '../firebase';
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import logger from '../utils/logger';

/**
 * Service for managing multi-family memberships.
 * Users can belong to multiple families and switch between them.
 *
 * Data model (per LAUNCH_ROADMAP.md):
 *   /users/{uid}.familyMemberships – Array of { familyCode, role, joinedAt }
 *   /users/{uid}.activeFamilyCode  – the active familyCode for this session
 *
 * Legacy fields (maintained for backward compatibility during migration):
 *   /users/{uid}.familyCode    – single family code (pre-B.1)
 *   /users/{uid}.families      – string[] (interim format)
 *   /users/{uid}.currentFamily – alias for activeFamilyCode
 */
class MultiFamilyService {
  /**
   * Get all families a user belongs to.
   * Reads from the user's `familyMemberships` array and enriches with family doc data.
   * Falls back to legacy `families` array or single `familyCode`.
   * @param {string} userId
   * @returns {Promise<Array<{familyCode: string, name: string, role: string, groups: string[], memberSince: Date, memberCount: number, isActive: boolean, isCurrent: boolean}>>}
   */
  async getUserFamilies(userId) {
    if (!userId) throw new Error('User ID is required');

    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return [];

    const userData = userDoc.data();

    // Determine membership list: prefer familyMemberships[], fall back to legacy formats
    let memberships = userData.familyMemberships || [];

    if (memberships.length === 0) {
      // Fall back to legacy `families` string array
      const legacyCodes = userData.families || [];
      if (legacyCodes.length > 0) {
        memberships = legacyCodes.map(code => ({
          familyCode: code,
          role: 'member',
          joinedAt: null
        }));
      }
    }

    if (memberships.length === 0) {
      // Fall back to single familyCode
      const legacyCode = userData.activeFamilyCode || userData.currentFamily || userData.familyCode;
      if (legacyCode) {
        memberships = [{ familyCode: legacyCode, role: userData.role || 'member', joinedAt: null }];
      }
    }

    const activeFamilyCode = userData.activeFamilyCode || userData.currentFamily || userData.familyCode;

    const results = [];
    for (const membership of memberships) {
      try {
        const familyDoc = await getDoc(doc(db, 'families', membership.familyCode));
        if (!familyDoc.exists()) continue;

        const familyData = familyDoc.data();
        // Get latest role from nested member doc (authoritative source)
        const memberDoc = await getDoc(doc(db, 'families', membership.familyCode, 'members', userId));
        const memberData = memberDoc.exists() ? memberDoc.data() : {};

        results.push({
          familyCode: membership.familyCode,
          name: familyData.name || `Family ${membership.familyCode}`,
          role: memberData.role || membership.role || 'member',
          groups: memberData.groups || [],
          memberSince: membership.joinedAt || memberData.joinedAt || familyData.createdAt,
          memberCount: familyData.memberCount || 0,
          isActive: familyData.isActive !== false,
          isCurrent: membership.familyCode === activeFamilyCode
        });
      } catch (err) {
        logger.error(`Error fetching family ${membership.familyCode}:`, err);
      }
    }

    return results;
  }

  /**
   * Switch the user's active family.
   * Updates `activeFamilyCode` and legacy `familyCode`/`currentFamily` on the user doc,
   * plus syncs role/groups from the target family's member doc.
   * @param {string} userId
   * @param {string} familyCode - The family to switch to
   * @returns {Promise<Object>} Updated user fields
   */
  async switchFamily(userId, familyCode) {
    if (!userId || !familyCode) throw new Error('User ID and family code are required');

    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) throw new Error('User not found');

    const userData = userDoc.data();

    // Verify user is a member of this family
    const memberships = userData.familyMemberships || [];
    const legacyCodes = userData.families || [];
    const isMember = memberships.some(m => m.familyCode === familyCode)
      || legacyCodes.includes(familyCode)
      || userData.familyCode === familyCode
      || userData.activeFamilyCode === familyCode
      || userData.currentFamily === familyCode;

    if (!isMember) {
      throw new Error('User is not a member of this family');
    }

    // Get role/groups from the target family's member doc
    const memberDoc = await getDoc(doc(db, 'families', familyCode, 'members', userId));
    if (!memberDoc.exists()) {
      throw new Error('Member record not found in target family');
    }

    const memberData = memberDoc.data();

    const updateData = {
      activeFamilyCode: familyCode,
      // Maintain legacy fields for backward compatibility
      currentFamily: familyCode,
      familyCode: familyCode,
      role: memberData.role || 'member',
      groups: memberData.groups || [],
      isAdmin: (memberData.groups || []).includes('ADMIN'),
      updatedAt: new Date()
    };

    await updateDoc(userRef, updateData);
    return updateData;
  }

  /**
   * Add a family to the user's familyMemberships array.
   * Called after joining or creating a new family.
   * @param {string} userId
   * @param {string} familyCode
   * @param {string} role - Role in the new family (default: 'member')
   */
  async addFamilyToUser(userId, familyCode, role = 'member') {
    if (!userId || !familyCode) return;

    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) return;

    const userData = userDoc.data();
    const memberships = userData.familyMemberships || [];

    // Don't add duplicate
    if (memberships.some(m => m.familyCode === familyCode)) return;

    const newMembership = {
      familyCode,
      role,
      joinedAt: new Date()
    };

    await updateDoc(userRef, {
      familyMemberships: arrayUnion(newMembership),
      // Maintain legacy field
      families: arrayUnion(familyCode),
      updatedAt: new Date()
    });
  }

  /**
   * Remove a family from the user's familyMemberships array.
   * Called after leaving a family.
   * @param {string} userId
   * @param {string} familyCode
   */
  async removeFamilyFromUser(userId, familyCode) {
    if (!userId || !familyCode) return;

    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) return;

    const userData = userDoc.data();
    const memberships = userData.familyMemberships || [];

    // Find and remove the membership entry
    const membershipToRemove = memberships.find(m => m.familyCode === familyCode);
    const updates = {
      families: arrayRemove(familyCode),
      updatedAt: new Date()
    };

    if (membershipToRemove) {
      updates.familyMemberships = arrayRemove(membershipToRemove);
    }

    // If this was the active family, switch to another one
    const activeFamilyCode = userData.activeFamilyCode || userData.currentFamily || userData.familyCode;
    if (activeFamilyCode === familyCode) {
      const remaining = memberships.filter(m => m.familyCode !== familyCode);
      if (remaining.length > 0) {
        updates.activeFamilyCode = remaining[0].familyCode;
        updates.currentFamily = remaining[0].familyCode;
        updates.familyCode = remaining[0].familyCode;
      } else {
        updates.activeFamilyCode = null;
        updates.currentFamily = null;
        updates.familyCode = null;
      }
    }

    await updateDoc(userRef, updates);
  }

  /**
   * Migrate a user from legacy data model to familyMemberships[].
   * Handles both single `familyCode` and `families[]` string array formats.
   * Idempotent – safe to call multiple times.
   * @param {string} userId
   */
  async migrateToMultiFamily(userId) {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) return;

    const userData = userDoc.data();

    // Already migrated to familyMemberships
    if (userData.familyMemberships && userData.familyMemberships.length > 0) return;

    // Collect all known family codes from legacy fields
    const codes = new Set();
    if (userData.families && userData.families.length > 0) {
      userData.families.forEach(c => codes.add(c));
    }
    const legacyCode = userData.currentFamily || userData.familyCode;
    if (legacyCode) codes.add(legacyCode);

    if (codes.size === 0) return;

    const familyMemberships = Array.from(codes).map(code => ({
      familyCode: code,
      role: code === legacyCode ? (userData.role || 'member') : 'member',
      joinedAt: new Date()
    }));

    const activeFamilyCode = legacyCode || Array.from(codes)[0];

    await updateDoc(userRef, {
      familyMemberships,
      activeFamilyCode,
      // Keep legacy fields for backward compat
      families: Array.from(codes),
      updatedAt: new Date()
    });
  }
}

export const multiFamilyService = new MultiFamilyService();
